import { AppError, extractMessage } from '@/helpers'

export class DelegateError extends AppError {
  constructor(options: { userMessage?: string; originalError?: unknown }) {
    super({
      title: 'Delegation failed',
      userMessage: options.userMessage,
      originalError: options.originalError,
      context: 'delegate',
    })
    this.name = 'DelegateError'
  }

  static fromUnknown(error: unknown): DelegateError {
    const raw = extractMessage(error)
    const lower = raw.toLowerCase()
    if (lower.includes('nft is delegated')) {
      return new DelegateError({
        userMessage: 'Shard is already delegated',
        originalError: error,
      })
    }
    if (lower.includes('insufficient funds')) {
      return new DelegateError({
        userMessage: 'Insufficient funds to cover fees',
        originalError: error,
      })
    }
    return new DelegateError({
      userMessage: raw || undefined,
      originalError: error,
    })
  }
}

export class UndelegateError extends AppError {
  constructor(options: { userMessage?: string; originalError?: unknown }) {
    super({
      title: 'Undelegation failed',
      userMessage: options.userMessage,
      originalError: options.originalError,
      context: 'undelegate',
    })
    this.name = 'UndelegateError'
  }

  static fromUnknown(error: unknown): UndelegateError {
    const raw = extractMessage(error)
    const lower = raw.toLowerCase()
    if (
      lower.includes('sender is not the owner') ||
      lower.includes('owner is invalid')
    ) {
      return new UndelegateError({
        userMessage: 'You are not the owner of this NFT',
        originalError: error,
      })
    }
    if (lower.includes('not delegated') || lower.includes('no delegation')) {
      return new UndelegateError({
        userMessage: 'No active delegation found for this NFT',
        originalError: error,
      })
    }

    if (lower.includes('too many unbonding delegation entries')) {
      return new UndelegateError({
        userMessage:
          'Too many undelegations are already in progress for this validator. Please wait for them to finish and try again.',
        originalError: error,
      })
    }

    if (lower.includes('negative coin amount')) {
      return new UndelegateError({
        userMessage:
          'Undelegation failed due to a chain error while calculating rewards. Please try again later.',
        originalError: error,
      })
    }

    if (lower.includes('insufficient funds')) {
      return new UndelegateError({
        userMessage: 'Insufficient funds to cover fees',
        originalError: error,
      })
    }
    return new UndelegateError({
      userMessage: raw || undefined,
      originalError: error,
    })
  }
}

export class RedelegateError extends AppError {
  constructor(options: { userMessage?: string; originalError?: unknown }) {
    super({
      title: 'Redelegation failed',
      userMessage: options.userMessage,
      originalError: options.originalError,
      context: 'redelegate',
    })
    this.name = 'RedelegateError'
  }

  static fromUnknown(error: unknown): RedelegateError {
    const raw = extractMessage(error)
    const lower = raw.toLowerCase()

    if (lower.includes('invalid creator address')) {
      return new RedelegateError({
        userMessage: 'Invalid creator address',
        originalError: error,
      })
    }
    if (lower.includes('invalid account address')) {
      return new RedelegateError({
        userMessage: 'Invalid NFT address',
        originalError: error,
      })
    }
    if (lower.includes('invalid validator address')) {
      return new RedelegateError({
        userMessage: 'Invalid validator address',
        originalError: error,
      })
    }
    if (lower.includes('invalid new validator address')) {
      return new RedelegateError({
        userMessage: 'Invalid new validator address',
        originalError: error,
      })
    }
    if (
      lower.includes('sender is not the owner') ||
      lower.includes('owner is invalid')
    ) {
      return new RedelegateError({
        userMessage: 'You are not the owner of this NFT',
        originalError: error,
      })
    }
    if (lower.includes('not delegated') || lower.includes('no delegation')) {
      return new RedelegateError({
        userMessage: 'No active delegation found for this NFT',
        originalError: error,
      })
    }
    if (lower.includes('insufficient funds')) {
      return new RedelegateError({
        userMessage: 'Insufficient funds to cover fees',
        originalError: error,
      })
    }

    if (lower.includes('cannot redelegate to the same validator')) {
      return new RedelegateError({
        userMessage: 'Cannot redelegate to the same validator',
        originalError: error,
      })
    }

    return new RedelegateError({
      userMessage: raw || undefined,
      originalError: error,
    })
  }
}

export class MintError extends AppError {
  constructor(options: { userMessage?: string; originalError?: unknown }) {
    super({
      title: 'Mint failed',
      userMessage: options.userMessage,
      originalError: options.originalError,
      context: 'mint',
    })
    this.name = 'MintError'
  }

  static fromUnknown(error: unknown): MintError {
    const raw = extractMessage(error)
    const lower = raw.toLowerCase()
    if (
      lower.includes('invalid owner address') ||
      lower.includes('invalid address') ||
      lower.includes('invalid bech32') ||
      lower.includes('decoding bech32 failed')
    ) {
      return new MintError({
        userMessage: 'Owner address is invalid',
        originalError: error,
      })
    }
    if (
      lower.includes('balance not found') ||
      lower.includes('insufficient pool balance')
    ) {
      return new MintError({
        userMessage: 'Not enough tokens in the Shard pool to mint a new Shard',
        originalError: error,
      })
    }

    if (lower.includes('not authorized') || lower.includes('unauthorized')) {
      return new MintError({
        userMessage: 'You are not authorized to mint',
        originalError: error,
      })
    }
    if (lower.includes('insufficient funds')) {
      return new MintError({
        userMessage: 'Insufficient funds to cover fees',
        originalError: error,
      })
    }
    if (lower.includes('start block') && lower.includes('not available')) {
      return new MintError({
        userMessage: 'Start block is not available yet. Please try again.',
        originalError: error,
      })
    }
    return new MintError({
      userMessage: raw || undefined,
      originalError: error,
    })
  }
}

export class WithdrawalError extends AppError {
  constructor(options: { userMessage?: string; originalError?: unknown }) {
    super({
      title: 'Withdrawal failed',
      userMessage: options.userMessage,
      originalError: options.originalError,
      context: 'withdrawal',
    })
    this.name = 'WithdrawalError'
  }

  static fromUnknown(error: unknown): WithdrawalError {
    const raw = extractMessage(error)
    const lower = raw.toLowerCase()

    if (lower.includes('nft not found')) {
      return new WithdrawalError({
        userMessage: 'Shard not found',
        originalError: error,
      })
    }

    if (
      lower.includes('invalid owner') ||
      lower.includes('sender is not the owner') ||
      lower.includes('owner is invalid')
    ) {
      return new WithdrawalError({
        userMessage: 'You are not the owner of this Shard',
        originalError: error,
      })
    }

    if (lower.includes('is delegated')) {
      return new WithdrawalError({
        userMessage: 'Shard is delegated. Undelegate first.',
        originalError: error,
      })
    }

    if (lower.includes('exceeds the withdrawable limit')) {
      return new WithdrawalError({
        userMessage: 'Amount exceeds the available to withdraw',
        originalError: error,
      })
    }

    if (lower.includes('failed to distribute')) {
      return new WithdrawalError({
        userMessage: 'Failed to distribute to address',
        originalError: error,
      })
    }

    if (lower.includes('insufficient funds')) {
      return new WithdrawalError({
        userMessage: 'Insufficient funds to cover fees',
        originalError: error,
      })
    }

    return new WithdrawalError({
      userMessage: raw || undefined,
      originalError: error,
    })
  }
}

export class TopUpError extends AppError {
  constructor(options: { userMessage?: string; originalError?: unknown }) {
    super({
      title: 'Top up failed',
      userMessage: options.userMessage,
      originalError: options.originalError,
      context: 'top-up',
    })
    this.name = 'TopUpError'
  }

  static fromUnknown(error: unknown): TopUpError {
    const raw = extractMessage(error)
    const lower = raw.toLowerCase()

    if (lower.includes('nft not found')) {
      return new TopUpError({
        userMessage: 'Shard not found',
        originalError: error,
      })
    }

    if (lower.includes('fully unlocked')) {
      return new TopUpError({
        userMessage: 'Shard is fully unlocked and can not be topped up',
        originalError: error,
      })
    }

    if (
      lower.includes('sender is not the owner') ||
      lower.includes("nft's owner is invalid") ||
      lower.includes('owner is invalid') ||
      lower.includes('invalid owner')
    ) {
      return new TopUpError({
        userMessage: 'Only the Shard owner can top up this Shard',
        originalError: error,
      })
    }

    if (lower.includes('invalid denom')) {
      return new TopUpError({
        userMessage: 'Invalid token for this Shard',
        originalError: error,
      })
    }

    if (lower.includes('insufficient funds')) {
      return new TopUpError({
        userMessage: 'Insufficient funds to cover amount or fees',
        originalError: error,
      })
    }

    return new TopUpError({
      userMessage: raw || undefined,
      originalError: error,
    })
  }
}

export class CloseNftError extends AppError {
  constructor(options: { userMessage?: string; originalError?: unknown }) {
    super({
      title: 'Close failed',
      userMessage: options.userMessage,
      originalError: options.originalError,
      context: 'close-nft',
    })
    this.name = 'CloseNftError'
  }

  static fromUnknown(error: unknown): CloseNftError {
    const raw = extractMessage(error)
    const lower = raw.toLowerCase()

    if (lower.includes('nft not found')) {
      return new CloseNftError({
        userMessage: 'Shard not found',
        originalError: error,
      })
    }

    if (
      lower.includes('invalid nft closer') ||
      lower.includes('unauthorized')
    ) {
      return new CloseNftError({
        userMessage: 'Only the module admin can close a Shard',
        originalError: error,
      })
    }

    if (
      lower.includes('delegated or being unbonded') ||
      lower.includes('is delegated')
    ) {
      return new CloseNftError({
        userMessage:
          'Shard has an active delegation, unbonding or redelegation. Undelegate first.',
        originalError: error,
      })
    }

    if (lower.includes('insufficient funds')) {
      return new CloseNftError({
        userMessage: 'Insufficient funds to cover fees',
        originalError: error,
      })
    }

    return new CloseNftError({
      userMessage: raw || undefined,
      originalError: error,
    })
  }
}

export class ChangeOwnerError extends AppError {
  constructor(options: { userMessage?: string; originalError?: unknown }) {
    super({
      title: 'Send failed',
      userMessage: options.userMessage,
      originalError: options.originalError,
      context: 'send',
    })
    this.name = 'ChangeOwnerError'
  }

  static fromUnknown(error: unknown): ChangeOwnerError {
    const raw = extractMessage(error)
    const lower = raw.toLowerCase()

    if (lower.includes('recipient address is nft')) {
      return new ChangeOwnerError({
        userMessage: 'Recipient address cannot be an NFT',
        originalError: error,
      })
    }
    if (lower.includes('nft not found')) {
      return new ChangeOwnerError({
        userMessage: 'NFT not found',
        originalError: error,
      })
    }
    if (lower.includes('nft is delegated')) {
      return new ChangeOwnerError({
        userMessage: 'NFT is delegated. Undelegate first.',
        originalError: error,
      })
    }
    if (
      lower.includes('sender is not the owner') ||
      lower.includes('owner is invalid') ||
      lower.includes('invalid owner')
    ) {
      return new ChangeOwnerError({
        userMessage: 'You are not the owner of this NFT',
        originalError: error,
      })
    }
    if (
      lower.includes('invalid recipient address') ||
      lower.includes('invalid address') ||
      lower.includes('invalid bech32') ||
      lower.includes('decoding bech32 failed')
    ) {
      return new ChangeOwnerError({
        userMessage: 'Recipient address is invalid',
        originalError: error,
      })
    }
    if (lower.includes('insufficient funds')) {
      return new ChangeOwnerError({
        userMessage: 'Insufficient funds to cover fees',
        originalError: error,
      })
    }

    return new ChangeOwnerError({
      userMessage: raw || undefined,
      originalError: error,
    })
  }
}
